<script lang="ts">
  import { deleteAllSearchQueryStringParameter, deleteSearchQueryStringParameter, setQueryStringParameter } from "../../url";
  import { SearchState } from "../../services/search-service";
  import type { SearchStateModel } from "../../models/search.interface";
  import StationSuggestion from "./StationSuggestion.svelte";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let autocompleteUrl: URL;
  export let title = "Dataset Search";
  export let tooltip = "Enter a dataset name or ID and select from the autocomplete suggestions to search by dataset.";

  let datasetSearchInput: string;
  let params = [];
  let suggest = [];
  let hiLiteIndex = null;
  let selectingFromSuggestion = false;
  let submitted = false;
  let activeFetch: AbortController | null = null;

  SearchState.subscribe((search: SearchStateModel) => {
    datasetSearchInput = search.params["text"] ? search.params["text"].join() : "";
    params = search.params.getAll("text") ? search.params.getAll("text") : [];
  });

  async function handleDatasetInput() {
    submitted = false;

    if (activeFetch) {
      activeFetch.abort();
      activeFetch = null;
    }

    if (!datasetSearchInput) {
      suggest = [];
      return;
    }

    activeFetch = new AbortController();

    const queryParams = new URLSearchParams();
    queryParams.set("field", "datasets");
    queryParams.set("text", datasetSearchInput);

    fetch(`${autocompleteUrl}?${queryParams}`, { signal: activeFetch.signal })
      .then((autocompleteResp) => {
        if (!autocompleteResp.ok) throw new Error("Failed to fetch suggestions");
        return autocompleteResp.json();
      })
      .then((suggestions) => {
        if (!submitted) suggest = suggestions.results;
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error(err);
      });
  }

  function navigateList(e) {
    if (e.key === "ArrowDown" && (hiLiteIndex === null || hiLiteIndex < suggest.length - 1)) {
      hiLiteIndex === null ? hiLiteIndex = 0 : hiLiteIndex += 1;

    } else if (e.key === "ArrowUp" && hiLiteIndex !== null) {
      hiLiteIndex === 0 ? hiLiteIndex = suggest.length - 1 : hiLiteIndex -= 1;

    } else if (e.key === "Enter") {
      submitted = true;

      if (hiLiteIndex !== null) {
        const suggestion = suggest[hiLiteIndex];
        if (suggestion) setParamVal(suggestion.id);
      } else {
        setQueryStringParameter("text", datasetSearchInput, false);
        clearSuggestions();
      }
      e.preventDefault();

    } else {
      return;
    }
  }

  function setParamVal(suggestionId: string) {
    selectingFromSuggestion = true;
    deleteAllSearchQueryStringParameter("text");
    setQueryStringParameter("text", suggestionId, false);
    clearSuggestions();
    hiLiteIndex = null;
  }

  function clearSuggestions(): void {
    suggest = [];
    hiLiteIndex = null;
  }

  function handleSubmit(e) {
    if (e.submitter) setParamVal(e.submitter.id);
  }
</script>

<svelte:window on:keydown={navigateList} />

<li class="mb-1">
  <div class="pb-2">
    {title}
    <TooltipHybridButton
      searchUrlQueryParam="dataset"
      tooltip={tooltip}
    />
  </div>
  <form
    autocomplete="off"
    on:submit|preventDefault={handleSubmit}
  >
    <div class="form-floating margin-2">
      <input
        type="text"
        class="form-control nosubmit"
        id="floatingInputDataset"
        placeholder="Dataset Search"
        bind:value={datasetSearchInput}
        on:input={handleDatasetInput}
        on:blur={() => {
          setTimeout(() => {
            if (selectingFromSuggestion) { selectingFromSuggestion = false; return; }
            if (!datasetSearchInput) return;
            if (params.includes(datasetSearchInput)) return;
            setQueryStringParameter("text", datasetSearchInput, false);
          }, 200);
        }}
      >
      <label for="floatingInputDataset">Datasets</label>

      {#if suggest && suggest.length > 0}
        <ul class="suggestions list-group bg-white mt-1 small shadow-lg position-absolute">
          {#each suggest as s, i}
            <StationSuggestion suggestion={s} highlighted={i === hiLiteIndex} on:click={() => setParamVal(s.id)} />
          {/each}
        </ul>
      {/if}
    </div>
  </form>
  {#if $SearchState.params.has("text")}
    {#each params as p}
      <div class="d-flex py-1">
        <span style="overflow-x: hidden; text-overflow: ellipsis"
              class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1"
              role="status"
              aria-label="dataset search pill: {p}"
        >
          <button
            type="button"
            id="delete-dataset-{p}"
            class="btn p-0 m-0"
            on:click={() => deleteSearchQueryStringParameter('text', p)}
          >
            <i class="bi bi-x text-bg-dark" aria-label="Delete dataset {p}"></i>
          </button>
          {p}
        </span>
      </div>
    {/each}
  {/if}
</li>

<style>
  .suggestions {
    z-index: 3;
  }
</style>